import React from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/solid';
import { SideNavMode } from './SideNav';
import {
  ArrowsPointingOutIcon,
  CheckIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { SideNavElement } from './SideNavElement';

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

export const sideNavModes = (t: TFunction<'containerInterface'>) =>
  [
    {
      type: 'auto',
      name: t('auto'),
      icon: ArrowsPointingOutIcon,
    },
    {
      type: 'expanded',
      name: t('expanded'),
      icon: ChevronDoubleRightIcon,
    },
    {
      type: 'collapsed',
      name: t('collapsed'),
      icon: ChevronDoubleLeftIcon,
    },
  ] as const;

export function SideNavOptions({
  setMode,
  mode,
  isExpanded,
  onEditNav,
}: {
  setMode: (mode: SideNavMode) => void;
  mode: SideNavMode;
  isExpanded?: boolean;
  /** MI-212: present only with the dashboards uplift on. */
  onEditNav?: () => void;
}) {
  const { t } = useTranslation('containerInterface');
  return (
    <div className="z-[51] mt-auto flex flex-col justify-center gap-y-0 pt-2">
      <Menu as="div" className="relative flex w-full">
        <MenuButton className={'w-full'}>
          <SideNavElement
            isExpanded={isExpanded}
            mode={mode}
            item={{
              icon: 'SideBarMenuIcon',
              key: 'menu',
              name: t('menuOptions'),
              url: '',
              showLabel: true,
            }}
          />
        </MenuButton>

        <MenuItems className="absolute bottom-0 left-[100%] mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none dark:divide-gray-700 dark:bg-gray-800 dark:ring-gray-600">
          <div className="px-1 py-1">
            {sideNavModes(t).map((item) => {
              return (
                <MenuItem key={item.type}>
                  {({ focus }) => (
                    <button
                      className={`${
                        focus
                          ? 'bg-br-primary/10 text-br-primary dark:text-br-secondary'
                          : 'text-gray-900 dark:text-gray-100'
                      } group flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm`}
                      onClick={() => {
                        setMode(item.type);
                      }}
                    >
                      {<item.icon className="h-4 w-4 flex-shrink-0" />}
                      {item.name}
                      {mode === item.type && (
                        <CheckIcon className="ml-auto h-4 w-4" />
                      )}
                    </button>
                  )}
                </MenuItem>
              );
            })}
          </div>
          {onEditNav && (
            <div className="px-1 py-1">
              <MenuItem>
                {({ focus }) => (
                  <button
                    className={`${
                      focus
                        ? 'bg-br-primary/10 text-br-primary dark:text-br-secondary'
                        : 'text-gray-900 dark:text-gray-100'
                    } group flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm`}
                    onClick={onEditNav}
                  >
                    <PencilSquareIcon className="h-4 w-4 flex-shrink-0" />
                    {t('editNavigation')}
                  </button>
                )}
              </MenuItem>
            </div>
          )}
        </MenuItems>
      </Menu>
    </div>
  );
}
